/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getEmployee, getVendor } from 'src/ducks/selectors'
import { Email } from 'src/ducks/types'
import { EMAIL_TYPE, EMPLOYEE_ROLE_TYPE, PHONE_MASK_INPUT } from 'src/helpers'
import { Box, Grid, TextFieldLabel, Typography } from '../../../../../UI'
import { VendorContext } from '../../context'

const availableRoles = [EMPLOYEE_ROLE_TYPE.TERRITORY_VENDOR_MANAGER, EMPLOYEE_ROLE_TYPE.MANAGER, EMPLOYEE_ROLE_TYPE.SUPER_ADMIN]

/** Splits comma-separated CC input into trimmed addresses. */
const parseCcEmailsInput = (raw: string) =>
  raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0)

const firstPrimaryIndex = (emails: Email[] | undefined) =>
  emails?.findIndex((e) => e.emailType === EMAIL_TYPE.PRIMARY) ?? -1

/**
 * Builds the CC input value: every row except the first PRIMARY (shown in Email) and except BCC.
 * Extra PRIMARY rows from the backend are included so they remain editable visible.
 */
const getCcEmailsFieldValue = (emails: Email[] | undefined) => {
  if (!emails?.length)
    return ''
  const idx = firstPrimaryIndex(emails)
  const extras = emails.filter(
    (e, i) => e.emailType !== EMAIL_TYPE.BCC && i !== idx,
  )

  return extras.map((e) => e.email.trim()).join(', ')
}

/**
 * Keeps first PRIMARY fixed; reapplies textarea as ordered middle rows preserving each template type/id;
 * BCC appended last.
 */
const rebuildEmailsKeepingPrimaryAndBcc = (previous: Email[], ccInputRaw: string): Email[] => {
  const bcc = previous.filter((e) => e.emailType === EMAIL_TYPE.BCC)
  const firstPriIdx = firstPrimaryIndex(previous)
  const canonicalPrimary = firstPriIdx >= 0 ? previous[firstPriIdx] : null

  const middleTemplates = previous.filter(
    (e, i) => i !== firstPriIdx && e.emailType !== EMAIL_TYPE.BCC,
  )

  const parts = parseCcEmailsInput(ccInputRaw)

  const middle = parts.map((emailAddr, idx) => {
    const tpl = middleTemplates[idx]

    return {
      id: tpl?.id ?? '',
      name: tpl?.name ?? '',
      description: tpl?.description ?? '',
      email: emailAddr,
      emailType: tpl?.emailType ?? EMAIL_TYPE.CC,
    }
  })

  return [...(canonicalPrimary ? [canonicalPrimary] : []), ...middle, ...bcc]
}

const Contact: FC = () => {
    const vendor = useSelector(getVendor())
    const { editedVendor, setEditedVendor } = useContext(VendorContext)
    const employee = useSelector(getEmployee)
    const disabled = !employee.roles.some((role) => availableRoles.includes(role.roleType))

    const [disabledSuperAdmin] = useState(!employee.roles.some(role => role.roleType === EMPLOYEE_ROLE_TYPE.SUPER_ADMIN || role.roleType === EMPLOYEE_ROLE_TYPE.TERRITORY_VENDOR_MANAGER))
    /** Raw CC string — must not derive from parsed emails alone or commas mid-type get stripped before the next segment exists. */
    const [ccEmailsRaw, setCcEmailsRaw] = useState<string>(() =>
        getCcEmailsFieldValue(editedVendor?.email ?? vendor?.email))

    useEffect(() => {
        setCcEmailsRaw(getCcEmailsFieldValue(editedVendor?.email ?? vendor?.email))
    }, [editedVendor?.id, vendor?.id])

    useEffect(() => {
        if (vendor?.id !== editedVendor?.id)
            setEditedVendor(vendor)

    }, [])

    return (

        <Box>
            <Typography variant='h4Bold' paddingY={2} display="block">Information</Typography>
            <Grid container spacing={2}>
                <Grid item xs={6} gap={2} display="flex" direction="column" container>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextFieldLabel
                                size='small'
                                value={editedVendor?.firstName}
                                variant='outlined'
                                label="Contact First Name:"
                                labelVariant='h5'
                                disabled={disabled}
                                onChange={(event) => {
                                    setEditedVendor({
                                        ...editedVendor,
                                        firstName: event.target.value
                                    })
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextFieldLabel
                                size='small'
                                value={editedVendor?.lastName}
                                variant='outlined'
                                label="Contact Last Name:"
                                labelVariant='h5'
                                disabled={disabled}
                                onChange={(event) => {
                                    setEditedVendor({
                                        ...editedVendor,
                                        lastName: event.target.value
                                    })
                                }}
                            />
                        </Grid>
                    </Grid>



                    <TextFieldLabel
                        size='small'
                        value={
                            editedVendor?.email?.find((e) => e.emailType === EMAIL_TYPE.PRIMARY)
                                ?.email ?? '-'
                        }
                        variant='outlined'
                        label="Email:"
                        labelVariant='h5'
                        disabled={disabledSuperAdmin}
                        textFieldStyle={{ color: '#3A66FF' }}
                        onChange={(event) => {
                            const nextEmails = [...(editedVendor?.email ?? vendor?.email ?? [])]
                            const primaryIdx = nextEmails.findIndex(
                                (e) => e.emailType === EMAIL_TYPE.PRIMARY,
                            )

                            if (primaryIdx >= 0) {
                                nextEmails[primaryIdx] = {
                                    ...nextEmails[primaryIdx],
                                    email: event.target.value,
                                }
                            } else {
                                const template = vendor?.email?.find(
                                    (e) => e.emailType === EMAIL_TYPE.PRIMARY,
                                )
                                nextEmails.unshift({
                                    ...(template ?? {
                                        id: '',
                                        name: '',
                                        description: '',
                                    }),
                                    email: event.target.value,
                                    emailType: EMAIL_TYPE.PRIMARY,
                                })
                            }

                            setEditedVendor({
                                ...editedVendor,
                                email: nextEmails,
                            })
                        }}
                    />
                </Grid>
                <Grid item xs={6} gap={2} display="flex" direction="column">
                    <TextFieldLabel
                        size='small'
                        value={editedVendor?.companyName}
                        variant='outlined'
                        label="Company Name:"
                        labelVariant='h5'
                        disabled={disabled}
                        onChange={(event) => {
                            setEditedVendor({
                                ...editedVendor,
                                companyName: event.target.value
                            })
                        }}
                    />
                    <TextFieldLabel
                        size='small'
                        value={ccEmailsRaw}
                        variant='outlined'
                        label="CC Emails:"
                        labelVariant='h5'
                        disabled={disabled}
                        placeholder="name@example.com, name@example.com"
                        helperText="Separate multiple email addresses with commas."
                        onChange={(event) => {
                            const raw = event.target.value
                            setCcEmailsRaw(raw)
                            const base = [...(editedVendor?.email ?? vendor?.email ?? [])]

                            setEditedVendor({
                                ...editedVendor,
                                email: rebuildEmailsKeepingPrimaryAndBcc(base, raw),
                            })
                        }}
                    />
                </Grid>
                <Grid item xs={6} gap={2} display="flex" direction="column">
                    <TextFieldLabel
                        size='small'
                        mask={PHONE_MASK_INPUT}
                        value={
                            editedVendor?.phone != null && `${editedVendor.phone}`.trim() !== ''
                                ? `${editedVendor.phone}`.replace(/\D/g, '')
                                : ''
                        }
                        variant='outlined'
                        style={{ marginBottom: '8px' }}
                        label="Phone:"
                        labelVariant='h5'
                        disabled={disabled}
                        placeholder="555-555-5555"
                        onChange={(event) => {
                            const digits = event.target.value.replace(/\D/g, '')
                            const phone = digits === '' ? undefined : Number(digits)

                            setEditedVendor({
                                ...editedVendor,
                                phone,
                            })
                        }}
                    />
                </Grid>
            </Grid>

            <Typography variant='h4Bold' paddingY={2} display="block">Address</Typography>

            <Grid container spacing={2}>
                <Grid item xs={6} gap={2} display="flex" direction="column">
                    <TextFieldLabel
                        size='small'
                        value={editedVendor?.address?.line1}
                        variant='outlined'
                        label="Street Address (line 1):"
                        labelVariant='h5'
                        disabled={disabled}
                        onChange={(event) => {
                            setEditedVendor({
                                ...editedVendor,
                                address: {
                                    ...editedVendor?.address as any,
                                    line1: event.target.value
                                }
                            })
                        }}
                    />
                    <TextFieldLabel
                        size='small'
                        value={editedVendor?.address?.city}
                        variant='outlined'
                        label="City:"
                        labelVariant='h5'
                        disabled={disabled}
                        onChange={(event) => {
                            setEditedVendor({
                                ...editedVendor,
                                address: {
                                    ...editedVendor?.address as any,
                                        city: event.target.value
                                    }
                                })
                        }}
                    />
                </Grid>
                <Grid item xs={6} gap={2} display="flex" direction="column">
                    <TextFieldLabel
                        size='small'
                        value={editedVendor?.address?.line2}
                        variant='outlined'
                        label="Street Address (line 2):"
                        labelVariant='h5'
                        disabled={disabled}
                        onChange={(event) => {
                            setEditedVendor({
                                ...editedVendor,
                                address: {
                                    ...editedVendor?.address as any,
                                        line2: event.target.value
                                    }
                                })
                        }}
                    />
                    <Grid container item spacing={2}>
                        <Grid item xs={8}>
                            <TextFieldLabel
                                size='small'
                                value={editedVendor?.address?.state}
                                variant='outlined'
                                label="State:"
                                labelVariant='h5'
                                disabled={disabled}
                                onChange={(event) => {
                                    setEditedVendor({
                                        ...editedVendor,
                                        address: {
                                            ...editedVendor?.address as any,
                                                state: event.target.value
                                            }
                                        })
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextFieldLabel
                                size='small'
                                value={editedVendor?.address?.zipCode}
                                variant='outlined'
                                label="ZIP Code:"
                                labelVariant='h5'
                                disabled={disabled}
                                onChange={(event) => {
                                    setEditedVendor({
                                        ...editedVendor,
                                        address: {
                                            ...editedVendor?.address as any,
                                                zipCode: event.target.value
                                            }
                                        })
                                }}
                            />
                        </Grid>
                    </Grid>

                </Grid>
            </Grid>
        </Box >
    )
}

export default Contact
